import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { Spinner } from "flowbite-react";
import useSize from "../../../../Utils/useSize/useSize";
import Backend from "../../../../Backend/Backend";
import Loading from "../../../../Utils/Loading/Loading";
import { QRCodeSVG } from "qrcode.react";

const backend = await new Backend().init();

export default function Deposit() {
  const windowSize = useSize();
  const [utr, setUtr] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [uploadImage, setUploadImage] = useState(null);
  const [bankData, setBankData] = useState({});
  const [showAmountModal, setShowAmountModal] = useState(true);
  const [loading, setLoading] = useState(true);

  const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];

  // =============================
  // Load Bank Data
  // =============================
  const loadBankData = useCallback(async () => {
    setLoading(true);

    let data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      user_id: localStorage.getItem("id"),
      token: localStorage.getItem("token"),
      username: localStorage.getItem("username"),
    };

    try {
      const r = await backend.bank_account(data);

      if (r.error === "False") {
        if (!r.account || Object.keys(r.account).length === 0) {
          toast.error("No active bank account found.");
          setBankData({});
        } else {
          setBankData(r.account);
        }
      } else {
        if (r.logout === 1) {
          window.LogOut();
        } else {
          toast.error(r.message);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error("Network Error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBankData();
  }, [loadBankData]);

  // =============================
  // Amount Submit
  // =============================
  const handleAmountSubmit = () => {
    const amt = parseFloat(depositAmount);

    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount!");
      return;
    }

    if (amt < 500) {
      toast.error("Minimum deposit ₹500 required");
      return;
    }

    setShowAmountModal(false);
    setSelectedOption("upi");
  };

  // =============================
  // Payment Method Select
  // =============================
  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setUploadImage(null);
  };

  // =============================
  // Image Upload
  // =============================
  const handleImageUpload = (e) => {
    const file = e.target.files[0];

    if (file && !allowedTypes.includes(file.type)) {
      toast.error("Only JPG and PNG images are allowed!");
      return;
    }

    setUploadImage(file);
  };

  // =============================
  // Submit Deposit
  // =============================
  const handleSubmit = async () => {
    if (
      !uploadImage ||
      !depositAmount ||
      !utr ||
      !selectedOption ||
      !bankData
    ) {
      toast.error("Please complete all fields and upload proof!");
      return;
    }

    setLoading(true);

    try {
      let data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        token: localStorage.getItem("token"),
        username: localStorage.getItem("username"),
        bank_id: bankData.id,
        utr: utr,
        amount: depositAmount,
      };

      const formData = new FormData();
      formData.append("utr", utr);
      formData.append("bank_id", bankData.id);
      formData.append("amount", depositAmount);
      formData.append("method", selectedOption);
      formData.append("file", uploadImage);

      const r = await backend.upload_payment_amount_ss(formData, data);

      if (r.error === "False") {
        toast.success(r.message);
      } else {
        toast.error(r.message);
      }

      setDepositAmount("");
      setUtr("");
      setSelectedOption(null);
      setUploadImage(null);
      setShowAmountModal(true);
    } catch {
      toast.error("Network error, please retry.");
    } finally {
      setLoading(false);
    }
  };

  const copyUpiId = async () => {
    if (!bankData?.upi_id) return;

    try {
      // Modern method
      await navigator.clipboard.writeText(bankData.upi_id);
      toast.success("UPI ID copied!");
    } catch (err) {
      // Fallback method (works everywhere)
      const textArea = document.createElement("textarea");
      textArea.value = bankData.upi_id;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
        toast.success("UPI ID copied!");
      } catch {
        toast.error("Copy failed. Please copy manually.");
      }

      document.body.removeChild(textArea);
    }
  };

  // =============================
  // Main UI
  // =============================

  const contentHeight = windowSize[1] - 90;
  const contentWidth = windowSize[0] - 30;

  // =============================
  // Loading Screen
  // =============================
  if (loading) {
    return <Loading height={windowSize[1] - 140} />;
  }

  // =============================
  // No Bank Account Screen
  // =============================
  if (!loading && Object.keys(bankData).length === 0) {
    return (
      <div
        style={{
          height: contentHeight,
        }}
        className="flex items-center justify-center p-8  bg-[var(--hover)]"
      >
        <div className="bg-[var(--background)] p-8 rounded-2xl shadow-lg text-center w-[400px]">
          <h3 className="text-xl font-semibold text-[var(--sell)]">
            No Active Bank Account Found
          </h3>
          <p className="text-sm mt-3 text-[var(--text)]">
            Please contact admin to activate deposit account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        height: contentHeight,
      }}
      className="flex items-center justify-center p-8 bg-[var(--hover)]"
    >
      <div
        style={{
          width: contentWidth,
        }}
        className=" bg-[var(--background)] dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
      >
        {/* STEP 1: Enter Amount */}
        {showAmountModal && (
          <div className="flex flex-col gap-4">
            <h3 className="text-center text-[var(--text)] text-lg font-semibold">
              Enter Deposit Amount
            </h3>

            <input
              type="number"
              value={depositAmount}
              placeholder="₹500 minimum"
              onChange={(e) => setDepositAmount(e.target.value)}
              className="bg-[var(--background)] text-[var(--text)] border border-[--hover] rounded-lg p-2 text-center focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
            />

            <button
              onClick={handleAmountSubmit}
              className="bg-[var(--primary)] text-[var(--white)] text-white rounded-lg py-2 font-semibold transition"
            >
              Continue
            </button>
          </div>
        )}

        {/* STEP 2: Payment Method */}
        {!showAmountModal && (
          <>
            <div className="bg-[var(--primary)] text-[var(--white)] text-center rounded-lg  py-2 mb-4 font-semibold">
              Deposit Amount: ₹{depositAmount}
            </div>

            <div className="flex gap-3 mb-4">
              <button
                onClick={() => handleOptionSelect("upi")}
                className={`flex-1 rounded-lg py-2 border transition ${
                  selectedOption === "upi"
                    ? "bg-[var(--primary)] text-white border-[var(--hover)]"
                    : "bg-[var(--white)] text-[var(--primary)] border-[var(--hover)]"
                }`}
              >
                Pay via UPI
              </button>

              <button
                onClick={() => handleOptionSelect("bank")}
                className={`flex-1 rounded-lg py-2 border transition ${
                  selectedOption === "bank"
                    ? "bg-[var(--primary)] text-white border-[var(--hover)]"
                    : "bg-[var(--white)] text-[var(--primary)] border-[var(--hover)]"
                }`}
              >
                Bank Transfer
              </button>
            </div>

            {/* UPI Section */}
            {selectedOption === "upi" && (
              <div className="flex flex-col items-center text-[var(--text)] text-center space-y-3">
                <p
                  onClick={copyUpiId}
                  className="cursor-pointer text-[var(--text)] bg-[var(--background)] px-4 py-1 rounded-lg border"
                >
                  <b>UPI ID: {bankData.upi_id}</b>
                </p>

                <QRCodeSVG value={bankData?.qr_name || ""} size={180} />

                <label className="font-semibold text-[var(--text)]">
                  Upload Screenshot
                </label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />

                <div className="flex flex-col gap-2">
                  <h3 className="text-center text-[var(--text)] text-[12px] font-semibold">
                    ENTER UTR ID
                  </h3>

                  <input
                    type="number"
                    value={utr}
                    placeholder="UTR ID"
                    onChange={(e) => setUtr(e.target.value)}
                    className="bg-[var(--background)] text-[var(--text)] border border-[--hover] rounded-lg p-2 text-center focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                  />
                </div>
              </div>
            )}

            {/* Bank Section */}
            {selectedOption === "bank" && (
              <div className="space-y-2 text-sm text-[var(--text)]">
                <p>
                  <b>Bank:</b> {bankData.bank}
                </p>
                <p>
                  <b>Account Name:</b> {bankData.account_name}
                </p>
                <p>
                  <b>Account Number:</b> {bankData.account_number}
                </p>
                <p>
                  <b>IFSC:</b> {bankData.account_ifsc}
                </p>

                <label className="font-semibold block mt-2">
                  Upload Screenshot
                </label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />

                <div className="flex flex-col gap-2">
                  <h3 className="text-center text-[var(--text)] text-[12px] font-semibold">
                    ENTER UTR ID
                  </h3>

                  <input
                    type="number"
                    value={utr}
                    placeholder="UTR ID"
                    onChange={(e) => setUtr(e.target.value)}
                    className="bg-[var(--background)] text-[var(--text)] border border-[--hover] rounded-lg p-2 text-center focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                  />
                </div>
              </div>
            )}

            {selectedOption && (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="mt-5 w-full bg-[var(--primary)] text-[var(--white)] rounded-lg py-3 font-semibold flex justify-center items-center transition disabled:opacity-70"
              >
                {loading ? <Spinner size="sm" /> : "Submit Deposit"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
